import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect } from 'react';
import { FullScreenSpinner } from 'smoothie';

import type { ContainerComponent } from '../common/types/Common';
import { adminUserAtom, allGuidesAtom } from '../entities/entityAtoms';
import { type Guide, type GuideDetails } from '../entities/entityTypes';
import { requestBuilder } from '../http/RequestBuilder';

const useGetAllGuides = requestBuilder.query<GuideDetails[]>({
    endpoint: '/api/admin/guides',
    queryKey: 'getAllGuides',
});

const getFullName = <
    T extends { firstName: string | null; lastName: string | null },
>(
    guide: T,
) => {
    const firstName = guide.firstName?.trim() ?? '';
    const lastName = guide.lastName?.trim() ?? '';
    return `${firstName} ${lastName}`.trim();
};

export const transposeGuide = (guide: Guide): GuideDetails => {
    return {
        ...guide,
        fullName: getFullName(guide),
    };
};

export const GuideEntityProvider: ContainerComponent = ({ children }) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canFetchGuides = adminUser.authorization.guides.canViewAll;
    const [allGuides, setAllGuides] = useAtom(allGuidesAtom);

    const { data: guides, isLoading: isLoadingGuides } = useGetAllGuides(null, {
        enabled: canFetchGuides,
        select: useCallback((data: Guide[]) => {
            return data.map(transposeGuide);
        }, []),
    });

    useEffect(() => {
        if (guides !== undefined) {
            setAllGuides(guides);
        }
    }, [guides]);

    if (canFetchGuides && allGuides.length === 0 && isLoadingGuides) {
        return <FullScreenSpinner />;
    }

    return <>{children}</>;
};
